import {
  ChangeDetectionStrategy,
  Component, computed,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import {FormGroup} from "@angular/forms";
import {RecordData, RecordUpdateRequestParams, SelectData} from "../../../../../types/records-table.types";
import {
  contactFormFields, contactResourceType,
  deleteConfirmationParams,
  unbindConfirmationParams
} from "../../configs/contacts-modal-config";
import {ContactFormFields, ContactsDialogData} from "../../types/contacts-modal.types";
import {ActionDropdownMenuComponent} from "../../../../action-dropdown-menu/action-dropdown-menu.component";
import {MatButton} from "@angular/material/button";
import {ContactsModalService} from "../../services/contacts-modal.service";
import {BaseModalService} from "../../../services/base-modal.service";
import {CloseModalReason} from "../../../../../types/modal.types";
import {ModalsService} from "../../../../../services/modals.service";
import {GetRecordPropertyDataByIdParams} from "../../../base-modal/types/base-modal.types";
import {defaultRecordData} from "../../../../../models/default-record-data";
import {FormContactsModalComponent} from "../form-contacts-modal/form-contacts-modal.component";
import {FormFieldUpdate} from "../../../../../types/file.types";
import {DetachProps} from "../../../../../types/attach-detach.types";
import {MatExpansionModule} from "@angular/material/expansion";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-current-contacts-modal',
  standalone: true,
  imports: [
    ActionDropdownMenuComponent,
    MatButton,
    FormContactsModalComponent,
    MatExpansionModule,
    LoaderComponent
  ],
  templateUrl: './current-contacts-modal.component.html',
  styleUrls: [
    './current-contacts-modal.component.scss',
    '../../contacts-modal.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrentContactsModalComponent implements OnInit {
  contactsDialogData = input.required<ContactsDialogData>();
  telegramAccountIds = input.required<Array<SelectData>>();
  isEditModeActive = input.required<boolean>();
  unbindContact = output<void>();
  closeDialog = output<CloseModalReason>();

  private contactsModalService: ContactsModalService = inject(ContactsModalService);
  private baseModalService: BaseModalService = inject(BaseModalService);
  private modalsService: ModalsService = inject(ModalsService);

  public contactFormGroups = signal<Array<FormGroup>>([]);
  public contactsData = signal<Array<RecordData>>([]);
  public isContactFormValid = signal<boolean>(false);
  public recordCalendarId = computed(() => this.baseModalService.getRecordCalendarId(this.contactsDialogData()?.recordData || defaultRecordData));
  public bloggerId = computed(() => this.contactsDialogData()?.bloggerId || 0);

  public async ngOnInit(): Promise<void> {
    this.contactsDialogData()?.fromBloggerProfile ?
      await this.fetchContactsData() :
      await this.fetchContactDataById();
  }

  public async handleDeleteOrUnbind(index?: number): Promise<void> {
    index !== undefined ?
      await this.handleDeleteContactClick(index) :
      await this.handleUnbindContactClick();
  }

  private async fetchContactsData(): Promise<void> {
    const data = await this.contactsModalService.fetchContactsData(this.contactsDialogData().bloggerId)
      .catch(() => []);
    this.contactsData.set(data);
    const formGroups = this.contactsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<ContactFormFields>(contactFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });
    this.contactFormGroups.set(formGroups);
    this.isContactFormValid.set(true);
  }

  private async fetchContactDataById(): Promise<void> {
    const getRecordPropertyDataByIdParams: GetRecordPropertyDataByIdParams = {
      path: contactResourceType.path,
      resourceId: Number((this.contactsDialogData()?.recordData['bloggerContact'] as RecordData).id),
    };

    const data = await this.contactsModalService.fetchContactDataById<RecordData>(getRecordPropertyDataByIdParams);
    this.contactsData.update((current) => [...current, data]);
    const formGroup = this.baseModalService.getFormGroup<ContactFormFields>(contactFormFields);
    formGroup.patchValue(data);

    this.contactFormGroups.update((current) => [...current, formGroup]);
    this.isContactFormValid.set(true);
  }

  public handleUpdateData(formField: FormFieldUpdate, index: number): void {
    if (!this.contactsDialogData()?.recordData['bloggerContact']) { return; }

    const recordUpdateRequestParams: RecordUpdateRequestParams = {
      path: contactResourceType.path,
      recordId: Number(this.contactsData().at(index)?.id),
      fieldName: formField.key,
      value: formField.newValue,
    };

    this.contactsModalService.updateContactData(recordUpdateRequestParams)
      .then((updateData) => {
        this.contactFormGroups().at(index)?.patchValue(updateData);
        this.contactsData.update(data => data.map((item, i) => i === index ? updateData : item));
      });
  }

  private async handleUnbindContactClick(): Promise<void> {
    const result = await this.modalsService.openBaseConfirmModalComponent(unbindConfirmationParams);
    result === 'remove' && await this.handleUnbindContact()
  }

  private async handleDeleteContactClick(index: number): Promise<void> {
    const result = await this.modalsService.openBaseConfirmModalComponent(deleteConfirmationParams);
    result === 'remove' && await this.deleteContactData(index);
  }

  private async deleteContactData(index: number): Promise<void> {
    const contactData = this.contactsData().at(index) as RecordData;
    await this.contactsModalService.deleteData(contactResourceType.path, contactData.id);
    this.contactFormGroups.update((groups) =>
      groups.filter((_, i) => i !== index)
    );
    this.contactsData.update((data) => data.filter((_, i) => i !== index));
  }

  private async handleUnbindContact(): Promise<void> {
    const detachProps: DetachProps = {
      path: contactResourceType.path,
      recordCalendarId: this.recordCalendarId(),
    };

    await this.contactsModalService.detachContactData(detachProps).catch(() => defaultRecordData);
    this.unbindContact.emit();
  }
}
